<template>
  <div class="modal" tabindex="-1" role="dialog" backdrop="static" id="dashboardTileDetailModal" @keyup.esc="onEsc($event)">
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header header">
          <h3 class="modal-title">{{ tileTitle }}</h3>
          <button type="button" class="btn-close btn-close-white" @click="close" aria-label="Close"></button>
        </div>
        <div class="modal-body" v-if="detailData">

          <!-- day tiles: two big bars, an overlay of one bar makes no sense -->
          <div v-if="tile && tile.period === 'day'" class="day-compare">
            <div class="day-bar-block">
              <div class="day-bar-label">{{ $t('dashboardPanel.period.day') }}</div>
              <div class="day-bar-track">
                <div class="day-bar current" :style="{ height: dayBars.current + '%' }"></div>
              </div>
              <div class="day-bar-value">{{ sumDisplay(detailData.current) }}</div>
            </div>
            <div class="day-bar-block">
              <div class="day-bar-label">{{ $t('dashboardTileDetailModal.yesterday') }}</div>
              <div class="day-bar-track">
                <div class="day-bar previous" :style="{ height: dayBars.previous + '%' }"></div>
              </div>
              <div class="day-bar-value">{{ sumDisplay(detailData.previous) }}</div>
            </div>
          </div>

          <!-- week/month/year: overlay chart, previous period pale -->
          <column-chart v-else :data="chartSeries" :library="chartLibraryOptions" height="320px" />

          <table class="detail-table">
            <tr>
              <td>{{ $t('dashboardTileDetailModal.currentSum') }}</td>
              <td class="num">{{ sumDisplay(detailData.current) }}</td>
            </tr>
            <tr>
              <td>{{ $t('dashboardTileDetailModal.previousSum') }}</td>
              <td class="num">{{ sumDisplay(detailData.previous) }}</td>
            </tr>
            <tr v-if="detailData.previousToDateSum != null">
              <td>{{ $t('dashboardTileDetailModal.previousToDateSum') }}</td>
              <td class="num">{{ $formatNumber(detailData.previousToDateSum, 2) }} {{ detailData.unit }}</td>
            </tr>
            <tr v-if="changeDisplay">
              <td>{{ $t('dashboardTileDetailModal.change') }}</td>
              <td class="num" :class="detailData.changePercent > 0 ? 'worse' : 'better'">{{ changeDisplay }}</td>
            </tr>
            <tr v-if="detailData.current && detailData.current.co2eSum != null">
              <td>{{ $t('dashboardTileDetailModal.co2e') }}</td>
              <td class="num">{{ $formatNumber(detailData.current.co2eSum, 2) }} kg</td>
            </tr>
            <tr v-if="detailData.current && detailData.current.costSum != null">
              <td>{{ $t('dashboardTileDetailModal.cost') }}</td>
              <td class="num">{{ $formatNumber(detailData.current.costSum, 2) }} €</td>
            </tr>
          </table>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="close">{{ $t('dashboardTileDetailModal.close') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from 'bootstrap'
import ModalViewMixin from '@/mixins/ModalViewMixin.js'
import { overlayChartSeries, formatChangePercent, miniBarWidths } from '@/utils/dashboard'

//
// Detail view of one period tile: the "nice comparison chart" — current
// period overlaid with the pale previous period (weekday-/day-of-month-/
// month-aligned), plus the sums, co2e and cost.
//
export default {
  name: 'DashboardTileDetailModal',
  mixins: [ModalViewMixin],
  props: {
    tile: {
      type: Object,
      default: null
    },
    detailData: {
      type: Object,
      default: null
    }
  },
  emits: ['modalClosed'],
  computed: {
    tileTitle () {
      return this.tile ? (this.tile.title || '') : ''
    },
    chartSeries () {
      if (!this.tile || !this.detailData) return []
      return overlayChartSeries(
        this.detailData,
        this.tile.period,
        this.$t('dashboardPanel.period.' + this.tile.period),
        this.$t('dashboardTileDetailModal.previousPeriod'))
    },
    chartLibraryOptions () {
      return {
        plugins: { legend: { display: true } },
        scales: {
          y: { title: { display: true, text: this.detailData ? this.detailData.unit : '' } }
        }
      }
    },
    dayBars () {
      if (!this.detailData || !this.detailData.current) return { current: 0, previous: 0 }
      return miniBarWidths(
        this.detailData.current.sum,
        this.detailData.previous ? this.detailData.previous.sum : null)
    },
    changeDisplay () {
      return this.detailData ? formatChangePercent(this.detailData.changePercent) : null
    }
  },
  methods: {
    sumDisplay (period) {
      if (!period || period.sum == null) return '—'
      return this.$formatNumber(period.sum, 2) + ' ' + (this.detailData.unit || '')
    },
    close () {
      this.emitClosed()
      Modal.getInstance(document.getElementById('dashboardTileDetailModal'))?.hide()
    }
  }
}
</script>

<style lang="scss" scoped>
.header {
  background-color: #6695c2;
  color: white;
}

.detail-table {
  width: 100%;
  margin-top: 16px;

  td {
    padding: 6px 10px;
    border-bottom: 1px solid var(--bs-border-color, #eee);
  }

  .num {
    text-align: right;
    font-weight: 600;
  }

  .worse { color: #ce2029; }
  .better { color: #2e8540; }
}

.day-compare {
  display: flex;
  justify-content: center;
  gap: 48px;
  padding: 12px 0;
}

.day-bar-block {
  text-align: center;
  width: 120px;
}

.day-bar-track {
  height: 180px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: #f6f8fa;
  border-radius: 6px;
}

[data-bs-theme="dark"] .day-bar-track {
  background: #3a3f45;
}

[data-bs-theme="dark"] .day-bar-label {
  color: #adb5bd;
}

.day-bar {
  width: 60px;
  border-radius: 4px 4px 0 0;
}

.day-bar.current { background: #6695c2; }
.day-bar.previous { background: #c3cede; }

.day-bar-label {
  font-size: 0.85em;
  color: #6c757d;
  margin-bottom: 4px;
}

.day-bar-value {
  font-weight: 700;
  margin-top: 6px;
}
</style>
